import { Component, ElementRef, HostListener, Injector, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Color, ScaleType } from '@swimlane/ngx-charts';
import { AbstractComponent, TaskDashboardReading, TaskDisciplineReading, TaskDisciplineRow, TasksService, TaskStreak } from 'lib-trend-core';
import { catchError, forkJoin, of, takeUntil } from 'rxjs';
import { TaskRangeComponent } from '../task-range/task-range.component';

type WindowDays = 7 | 30;

/**
 * The activities dashboard (SPEC-2026-10-crm-activities-cadence RF-46 to RF-49): the user's own
 * figures and streak on the dashboard's KPI tiles, conclusions per day on the chart the
 * dashboards use, and — for supervisors and administrators — the team's discipline in the
 * listing-table pattern, over a 7- or 30-day window chosen with the selector pattern.
 */
@Component({
  selector: 'app-task-dashboard',
  templateUrl: './task-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TaskDashboardComponent extends AbstractComponent implements OnInit {

  @ViewChild('chartContainer') chartContainer?: ElementRef<HTMLElement>;

  windowDays: WindowDays = 7;
  reading?: TaskDashboardReading;
  discipline?: TaskDisciplineReading;
  override loading = true;
  failed = false;
  disciplineFailed = false;

  chartData: Array<{ name: string; value: number }> = [];
  chartView: [number, number] = [600, 260];
  /** The theme's primary, read from the CSS custom property — never a literal. */
  colorScheme: Color = { name: 'activities', selectable: true, group: ScaleType.Ordinal, domain: [TaskDashboardComponent.token('--ct-primary', '#4440f7')] };

  constructor(injector: Injector, private readonly tasksService: TasksService) {
    super(injector);
  }

  ngOnInit(): void {
    this.load();
  }

  /** RF-49: discipline is for supervisors, administrators and root operators. */
  get canReadDiscipline(): boolean {
    return this.isRoot() || this.isAdmin() || this.isSupervisor();
  }

  get windowLabel(): string {
    return this.windowDays === 7 ? 'task.dashboard.window7' : 'task.dashboard.window30';
  }

  setWindow(days: WindowDays): void {
    if (days === this.windowDays) return;
    this.windowDays = days;
    this.load();
  }

  /** `N` or `N+` when the 120-day window was reached (RF-47). */
  streakLabel(streak?: TaskStreak): string {
    if (!streak) return '0';
    return `${streak.days}${streak.capped ? '+' : ''}`;
  }

  percent(rate: number | null): string {
    return rate === null || rate === undefined ? '—' : `${Math.round(rate * 100)}%`;
  }

  numberOrDash(value: number | null | undefined): string {
    return value === null || value === undefined ? '—' : `${value}`;
  }

  rowKey(_index: number, row: TaskDisciplineRow): string {
    return row.user._id;
  }

  @HostListener('window:resize')
  onResize(): void {
    this.resizeChart();
  }

  private load(): void {
    this.loading = true;
    const today = new Date();
    const from = new Date(today);
    from.setDate(from.getDate() - (this.windowDays - 1));
    const params = { from: TaskRangeComponent.dayKey(from), to: TaskRangeComponent.dayKey(today) };

    forkJoin({
      reading: this.tasksService.dashboard(params).pipe(catchError(() => of(null))),
      discipline: this.canReadDiscipline ? this.tasksService.discipline(params).pipe(catchError(() => of(null))) : of(undefined),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ reading, discipline }) => {
        this.loading = false;
        this.failed = !reading;
        this.disciplineFailed = this.canReadDiscipline && !discipline;
        if (reading) {
          this.reading = reading;
          this.chartData = this.fillDays(reading, params.from, params.to);
          setTimeout(() => this.resizeChart());
        }
        if (discipline) this.discipline = discipline;
      });
  }

  /** Every day of the window gets a bar, zero included — a gap is a day with no conclusion, not a missing day. */
  private fillDays(reading: TaskDashboardReading, from: string, to: string): Array<{ name: string; value: number }> {
    const counts = new Map((reading.concludedPerDay ?? []).map((row) => [row.day, row.count]));
    const data: Array<{ name: string; value: number }> = [];
    for (let date = new Date(`${from}T00:00:00`); TaskRangeComponent.dayKey(date) <= to; date.setDate(date.getDate() + 1)) {
      const day = TaskRangeComponent.dayKey(date);
      data.push({ name: `${`${date.getDate()}`.padStart(2, '0')}/${`${date.getMonth() + 1}`.padStart(2, '0')}`, value: counts.get(day) ?? 0 });
    }
    return data;
  }

  private resizeChart(): void {
    const width = this.chartContainer?.nativeElement?.clientWidth;
    if (width && width > 0) this.chartView = [width, 260];
  }

  private static token(name: string, fallback: string): string {
    if (typeof window === 'undefined' || typeof document === 'undefined') return fallback;
    const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return value || fallback;
  }
}
